import {GoogleLoginResponse, User, UserToken} from "@/types/user"
import axios, {AxiosResponse} from "axios"
import api from "./api"
import {getUser} from "./userService"
import {
  identifyOnAuth,
  resetUser,
} from "@/lib/posthog.ts";

const redirectToRoot = () => window.location.assign("/");

export const createUser = async (
    first_name: string,
    last_name: string,
    email: string,
    password: string
): Promise<AxiosResponse<User>> => {
  try {
    return await api.post<User>(`/auth/users/`, {first_name, last_name, email, password})
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data
      if (data && typeof data === "object") {
        const messages = Object.values(data).flat().map((msg) => `• ${msg}`).join("\n")
        throw new Error(messages)
      }
      throw new Error((data as any)?.detail || "Failed to create user")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const signInWithEmailAndPassword = async (
    email: string,
    password: string
): Promise<AxiosResponse<UserToken>> => {
  try {
    const response = await api.post<UserToken>(`/auth/jwt/create/`, { email, password })
    if (response.status === 200) {
      await refreshAccessToken()
      const token = response.data.access
      const meRes = await getUser(token)
      identifyOnAuth(meRes.data)
      redirectToRoot();
    }
    return response
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.detail || "Failed to sign in")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const authWithGoogle = async (
    credentialResponse: string
): Promise<AxiosResponse<GoogleLoginResponse>> => {
  try {
    const response = await api.post<GoogleLoginResponse>(
        `/auth/google-login/`,
        { credential: credentialResponse },
        { headers: { "Content-Type": "application/json" } }
    )
    if (response.status === 200) {
      await refreshAccessToken()
      const token = response.data.access
      const meRes = await getUser(token)
      identifyOnAuth(meRes.data)
      redirectToRoot();
    }
    return response
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.detail || "Failed to sign in with Google")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const signOut = async (token: string): Promise<void> => {
  try {
    const response = await api.post(
        `/auth/jwt/logout/`,
        {},
        { headers: { Authorization: `JWT ${token}` } }
    )
    if (response.status === 200) resetUser()
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.detail || "Failed to sign out")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const refreshAccessToken = async (): Promise<AxiosResponse<UserToken>> => {
  // Keep the Axios error intact. Auth bootstrap needs the response status to
  // distinguish a normal missing/expired session (401) from a real network or
  // server failure that should still be logged.
  return api.post<UserToken>(`/auth/jwt/refresh/`)
}

export const isRefreshSessionUnavailable = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 401

export const updatePassword = async (
    token: string | null,
    currentPassword: string,
    newPassword: string
): Promise<AxiosResponse<void>> => {
  try {
    if (!token) throw new Error("User is not authenticated")
    return await api.post<void>(
        `/auth/users/set_password/`,
        {current_password: currentPassword, new_password: newPassword},
        {headers: {Authorization: `JWT ${token}`}}
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.detail || "Failed to update password")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const activateUserEmail = async (uid: string, token: string): Promise<AxiosResponse<void>> => {
  try {
    return await api.post<void>(`/auth/users/activation/`, {uid, token})
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.detail || "Failed to activate email")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const resetPasswordSendEmail = async (email: string): Promise<AxiosResponse<void>> => {
  try {
    return await api.post<void>(`/auth/users/reset_password/`, {email})
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data.detail || "Failed to send reset password email")
    }
    throw new Error("An unexpected error occurred")
  }
}

export const resetPasswordConfirm = async (
    uid: string,
    token: string,
    newPassword: string
): Promise<AxiosResponse<void>> => {
  try {
    return await api.post<void>(`/auth/users/reset_password_confirm/`, {
      uid,
      token,
      new_password: newPassword,
    })
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Djoser reports password-policy failures as {"new_password": [...]}, not
      // as `detail`, so reading `detail` alone left the user with no reason.
      // Same shape as createUser above.
      const data = error.response?.data
      if (data && typeof data === "object") {
        const messages = Object.values(data).flat().map((msg) => `• ${msg}`).join("\n")
        if (messages) throw new Error(messages)
      }
      throw new Error((data as any)?.detail || "Failed to confirm password reset")
    }
    throw new Error("An unexpected error occurred")
  }
}
